import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Card";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../../components/Table";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { DBImportJob } from "@KassaroAB/shared/types/importJob";
import { JobStatus } from "@KassaroAB/shared/types/enums/JobStatus";
import { formatDate } from "../../../utils/dateUtils";

const HistoryContainer = styled.div`
  margin-top: ${spacing.x4}px;
`;

const CardTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

const CardDescription = styled.p`
  margin: 0 0 ${spacing.x3}px 0;
  max-width: 52rem;
  font-size: 14px;
  line-height: 1.55;
  color: ${colors.textMuted};
`;

const StatusBadge = styled.span<{ $variant: string }>`
  display: inline-block;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ $variant }) => {
    switch ($variant) {
      case "completed":
        return colors.success;
      case "failed":
        return colors.danger;
      case "processing":
        return colors.primary;
      default:
        return colors.border;
    }
  }};
  color: ${({ $variant }) =>
    $variant === "completed" || $variant === "failed" || $variant === "processing"
      ? "white"
      : colors.text};
`;

function jobStatusVariant(job: DBImportJob): "completed" | "failed" | "processing" | "pending" {
  if (job.status === JobStatus.FAILED) {
    return "failed";
  }
  if (job.status === JobStatus.COMPLETED) {
    return "completed";
  }
  if (job.status === JobStatus.PROCESSING) {
    return "processing";
  }
  return "pending";
}

function jobStatusKey(job: DBImportJob): "statusPending" | "statusProcessing" | "statusCompleted" | "statusFailed" {
  if (job.processedRows === job.totalRows && job.totalRows > 0) {
    return "statusCompleted";
  }
  switch (job.status) {
    case JobStatus.PENDING:
      return "statusPending";
    case JobStatus.PROCESSING:
      return "statusProcessing";
    case JobStatus.COMPLETED:
      return "statusCompleted";
    case JobStatus.FAILED:
      return "statusFailed";
    default:
      return "statusPending";
  }
}

interface ImportJobHistoryProps {
  jobs: DBImportJob[] | undefined;
  isLoading: boolean;
}

export const ImportJobHistory = ({ jobs, isLoading }: ImportJobHistoryProps) => {
  const { t } = useTranslation();

  if (isLoading && !jobs) {
    return null;
  }

  if (!jobs || jobs.length === 0) {
    return null;
  }

  return (
    <HistoryContainer>
      <Card>
        <CardTitle>{t("productImport.jobHistoryTitle")}</CardTitle>
        <CardDescription>{t("productImport.jobHistoryDescription")}</CardDescription>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t("productImport.jobHistoryStatus")}</TableHeaderCell>
              <TableHeaderCell>{t("productImport.jobHistoryProgress")}</TableHeaderCell>
              <TableHeaderCell>{t("productImport.jobHistoryStarted")}</TableHeaderCell>
              <TableHeaderCell>{t("productImport.jobHistoryCompleted")}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((job) => {
              const variant = jobStatusVariant(job);
              const sk = jobStatusKey(job);
              return (
                <TableRow key={job.id}>
                  <TableCell>
                    <StatusBadge $variant={variant}>{t(`productImport.${sk}`)}</StatusBadge>
                  </TableCell>
                  <TableCell>
                    {job.processedRows > 0 && job.totalRows > 0 ? (
                      <>
                        {job.processedRows} / {job.totalRows}
                        {job.status === JobStatus.COMPLETED && job.processedRows > 0 && (
                          <div style={{ fontSize: "12px", color: colors.textMuted }}>
                            {t("productImport.jobSucceeded", { count: job.processedRows })}
                          </div>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{formatDate(job.createdAt)}</TableCell>
                  <TableCell>{formatDate(job.completedAt)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </HistoryContainer>
  );
};
